import React, { useState, useEffect } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Eye, LayoutTemplate, Info, Save } from "lucide-react";
import { Button } from "../../components/ui/Button";
import ViewWebsiteButton from "../../components/ui/ViewWebsiteButton";
import { useAuth } from "../../auth/AuthContext";
import SectionHeader from "../../components/ui/SectionHeader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useToast } from "../../hooks/useToast";
import InfoContainer from "../../components/ui/InfoContainer";
import WebsiteSectionCard from "../../components/ui/WebsiteSectionCard";
import PageHeader from "../../components/ui/PageHeader";
import { getWebsiteSettings, updateCompanyTemplate } from "../../services/websitesetting";
import GlobalLoader from "../../components/ui/GlobalLoader";

/** Polished templates — others are marked Beta in the picker */
const IMPROVED_TEMPLATE_IDS = new Set([
  "index2",
  "index3",
  "index7",
  "index9",
  "index10",
  "index20",
]);

const templates = [
  { id: "index", name: "Men Barber Salon Website (1st Template)", description: "Classic barbershop layout with a masculine aesthetic." },
  { id: "index2", name: "Spa, Beauty & Skin Care Center Website", description: "Soft, relaxing design for wellness and beauty centers." },
  { id: "index3", name: "Ladies Salon Center Website", description: "Elegant and modern design tailored for women's beauty services." },
  { id: "index4", name: "Nail Salon Center Website", description: "Vibrant layout focused on showcasing nail art and services." },
  { id: "index5", name: "Makeup Beauty Center Website", description: "Chic and stylish design for professional makeup artists." },
  { id: "index6", name: "ladies Hair styling & Hair cut Saloon Website", description: "Focuses on hair transformation and styling excellence." },
  { id: "index7", name: "Men Barber Hair, Styling & Dressing Saloon Website (2nd Template)", description: "Modern take on the traditional barber experience." },
  { id: "index8", name: "Men Barber Hair, Styling & Dressing Saloon Website (3rd Template)", description: "Bold and edgy design for contemporary grooming hubs." },
  { id: "index9", name: "Aesthetic clinic Website (1st Template)", description: "Clean, clinical, and professional design for medical aesthetics." },
  { id: "index10", name: "Aesthetic clinic Website (2nd Template)", description: "Premium and sophisticated layout for high-end clinics." },
  { id: "index11", name: "Tattoo & Piercing Website (1st Template)", description: "Artistic and bold design to showcase portfolio work." },
  { id: "index12", name: "Tattoo & Piercing Website (2nd Template)", description: "Modern studio layout with high-impact visual elements." },
  { id: "index13", name: "Health & Fitness Website (1st Template)", description: "Energetic design for gyms and personal trainers." },
  { id: "index14", name: "Health & Fitness Website (2nd Template)", description: "Clean and motivating layout for wellness studios." },
  { id: "index15", name: "Dental & Orthodontics Website (1st Template)", description: "Trustworthy and professional medical design." },
  { id: "index16", name: "Dental & Orthodontics Website (2nd Template)", description: "Modern patient-focused layout for dental clinics." },
  { id: "index17", name: "Counseling & Holistic Website (1st Template)", description: "Calm and supportive design for therapy practices." },
  { id: "index18", name: "Counseling & Holistic Website (2nd Template)", description: "Mindfulness-focused layout for holistic centers." },
  { id: "index19", name: "Braids & Locs Website", description: "Specialized design for Locs and Braiding professionals." },
  { id: "index20", name: "Men & Women hair saloon Website", description: "Versatile layout for unisex hair and beauty lounges." },
].map((template) => ({
  ...template,
  beta: !IMPROVED_TEMPLATE_IDS.has(template.id),
}));

const TemplateSection: React.FC = () => {
  const { subdomain } = useOutletContext<{ subdomain: string }>();
  const toast = useToast();
  const [selectedTemplate, setSelectedTemplate] = useState("index");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        const data = await getWebsiteSettings();
        // Company active template (not about_section — that can be missing/stale)
        const templateId =
          data?.theme_template ||
          data?.website_theme_settings?.template ||
          data?.about_section?.template ||
          null;

        if (templateId && templates.some((t) => t.id === templateId)) {
          setSelectedTemplate(templateId);
        }
      } catch (error) {
        console.error("Failed to fetch initial template settings:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await updateCompanyTemplate(selectedTemplate);
      if (response.success !== false) {
        toast.success(response.message || "Template settings updated successfully!");
      } else {
        toast.error(response.message || "Failed to update template.");
      }
    } catch (error: any) {
      console.error("Failed to update template:", error);
      toast.error("An error occurred while saving template settings.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {(isLoading || isSaving) && (
        <GlobalLoader
          initialPageLoad={isLoading}
          fullScreen={isSaving}
          description={isSaving ? "Saving template settings..." : "Loading template settings..."}
        />
      )}
      <div className="space-y-5">
        {/* Header Info Card - Matching other sections */}
        <PageHeader
          title="Website Templates"
          description="Choose a professional layout that represents your brand"
          variant="card"
          className="mb-5"
          mobileActionsInHeader={true}
          actions={
            <ViewWebsiteButton subdomain={subdomain} />
          }
        />

        {/* Main Configuration Card */}
        <WebsiteSectionCard
          title="Template Configuration"
          icon={LayoutTemplate}
          bodyClassName="p-6 space-y-6"
        >
          <div>
            <SectionHeader title="Active Template" />

            <InfoContainer
              message="Changing your template updates the website layout while preserving all existing content and settings."
              iconSize={16}
            />

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Choose Template Layout <span className="text-red-500">*</span>
                </label>
                <div className="w-full">
                  <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                    <SelectTrigger className="w-full h-9 bg-white border-gray-200 focus:ring-2 focus:ring-[#885ABB]">
                      <SelectValue placeholder="Select a template" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {templates.map((template) => (
                        <SelectItem
                          key={template.id}
                          value={template.id}
                          textValue={`${template.name}${template.beta ? " (Beta)" : ""}`}
                        >
                          <div className="py-0.5">
                            <div className="flex items-center gap-2 text-sm font-normal text-gray-900">
                              <span>{template.name}</span>
                              {template.beta && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800">
                                  Beta
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-gray-400 font-normal mt-0">
                              {template.description}
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Enhanced Preview Action */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 mt-6 shadow-sm">
                <div className="flex items-center gap-4">
                  {/* Clickable Preview Button (Eye Icon) */}
                  <a
                    href={`https://${subdomain}.salonify.co.uk?mode=preview`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-10 px-4 bg-white rounded-lg border border-gray-200 hover:border-[#885ABB] flex items-center gap-2 transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md group flex-shrink-0"
                    title="Click to process Live Preview"
                  >
                    <Eye className="text-[#885ABB] w-4 h-4" />
                    <span className="text-xs font-medium text-[#885ABB]">Preview</span>
                  </a>

                  {/* Informational Text (Not Clickable) */}
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
                      <h4 className="text-sm font-medium text-gray-900">
                        Preview {templates.find(t => t.id === selectedTemplate)?.name}
                      </h4>
                      {templates.find((t) => t.id === selectedTemplate)?.beta && (
                        <span className="inline-flex max-w-fit items-center px-2 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 uppercase tracking-wide">
                          Beta
                        </span>
                      )}
                      <span className="inline-flex max-w-fit items-center px-2 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700 uppercase tracking-wide">
                        Live Preview
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      Click the eye icon to see how this template looks with your content in real-time.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="pt-6 border-t border-gray-100 flex justify-end">
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-8 py-2.5 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </WebsiteSectionCard>
      </div>
    </>
  );
};

export default React.memo(TemplateSection);
